import { fromArrayBuffer } from "geotiff";
import { PALETTE_LUT } from "./palette";
import { cornersFromBBox3857 } from "./geo";
import type { DecodedFrame } from "./frameCache";

/** Fetch a COG whole (30-160 KB) and decode+colorize it. */
export async function fetchAndDecodeFrame(url: string, signal?: AbortSignal): Promise<DecodedFrame> {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`frame fetch failed: ${url} (${res.status})`);
  const buf = await res.arrayBuffer();
  return decodeFrame(buf);
}

/**
 * band 1 (uint8, 0 = nodata) -> RGBA canvas via the NWS palette LUT, plus the
 * frame's EPSG:3857 bounding box converted to lon/lat corners for MapLibre.
 */
export async function decodeFrame(buf: ArrayBuffer): Promise<DecodedFrame> {
  const tiff = await fromArrayBuffer(buf);
  const image = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();
  const bbox = image.getBoundingBox() as [number, number, number, number];

  const rasters = await image.readRasters({ interleave: false });
  const band = rasters[0] as ArrayLike<number>;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  const imgData = ctx.createImageData(width, height);
  const px = imgData.data;
  for (let i = 0; i < band.length; i++) {
    const v = band[i] & 0xff;
    const o = i * 4;
    const li = v * 4;
    px[o] = PALETTE_LUT[li];
    px[o + 1] = PALETTE_LUT[li + 1];
    px[o + 2] = PALETTE_LUT[li + 2];
    px[o + 3] = PALETTE_LUT[li + 3];
  }
  ctx.putImageData(imgData, 0, 0);

  // toDataURL() is synchronous, so both the initial addSource() (which needs a
  // JSON-serializable `url`) and every later updateImage({url}) go through the
  // exact same MapLibre code path with no separate "decoded image" special case
  // to race against — addSource(placeholder) + immediate updateImage({image})
  // raced in practice (the placeholder's async load won, blanking the frame).
  const url = canvas.toDataURL("image/png");

  return { url, corners: cornersFromBBox3857(bbox), width, height };
}
